import React, { use, useEffect } from "react";
import { Tooltip, TooltipRefProps } from "react-tooltip";
import { MediaBase } from "../models";
import { auth } from "../firebase-config";
import { Toasts } from "../helpers/helpers";
import { addToWatchHistory, removeAllFromWatchHistory, removeFromWatchHistory } from "../helpers/watchHistoryHelpers";
import { useHistory } from "../hooks/HistoryContext";
import "../styles/WatchPopup.scss";

const WatchPopup = ({ anchorSelect, media, onWatched }: { anchorSelect: string, media: MediaBase, onWatched?: () => void }) => {
    const popupRef = React.useRef<TooltipRefProps>(null);
    const [watched, setWatched] = React.useState(false);
    const { history, fetchHistory } = useHistory();

    useEffect(() => {
        const isWatched = history.some(item => item.tmdb_id.toString() === media.id.toString());
        setWatched(isWatched);
    }, [history, media.id]);

    function content() {
        if (watched) {
            return watchAgain();
        } else {
            return unwatchedContent();
        }
    }

    // Get local time in "YYYY-MM-DD HH:MM:SS" format
    function getTime(date: Date) {
        const d = new Date(date);
        const pad = (n: number) => n.toString().padStart(2, '0');

        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
            `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    }
    
    function unwatchedContent() {
        return (
            <div className="watch-popup-content">
                <h3>Mark as Watched</h3>
                <button className="history-button" onClick={() => markAsWatched(getTime(new Date()))}>Now</button>
                <button onClick={() => markAsWatched(media.release_date)}>Release Date</button>
                <button onClick={() => Toasts.warn("Custom Dates not yet implemented.")}>Other</button>
            </div>
        );
    }
    
    function watchAgain() {
        return (
            <div className="watch-popup-content">
                <p>Remove from history?</p>
                {media.watch_id &&
                    <button onClick={() => removeFromHistory(false)}>This play</button>
                }
                <button onClick={() => removeFromHistory(true)}>All plays</button>
                <button onClick={() => setWatched(false)}>Watch Again</button>
            </div>
        );
    }

    async function markAsWatched(watchDate: string) {
        const user = auth.currentUser;
        if (!user) {
            Toasts.error("You must be logged in to use this feature.");
            return;
        }

        const response = await addToWatchHistory(user.uid, media.id.toString(), media.media_type, watchDate);
        if (response) {
            Toasts.success("Added to watch history.");
            popupRef.current?.close();
            onWatched?.();
            setWatched(true);
            await fetchHistory();
        } else {
            Toasts.error("Failed to add to watch history. Please try again later.");
        }
    }

    async function removeFromHistory(allPlays: boolean) {
        const user = auth.currentUser;
        if (!user) {
            Toasts.error("You must be logged in to use this feature.");
            return;
        }

        let response: Boolean = false;

        if (allPlays) {
            response = await removeAllFromWatchHistory(user.uid, media.id.toString());
        } else {
            response = await removeFromWatchHistory(user.uid, media.watch_id?.toString() || "");
        }
        
        if (response) {
            Toasts.success("Removed from watch history.");
            setWatched(false);
            await fetchHistory();
        } else {
            Toasts.error("Failed to remove from watch history. Please try again later.");
        }
    }

    return (
        <Tooltip 
            id="watch-tooltip"
            ref={popupRef}
            place="bottom"
            anchorSelect={anchorSelect}
            opacity={1}
            arrowSize={10}
            clickable={true}
            openEvents={{ click: true }}
            closeEvents={{ mouseout: true }}
            globalCloseEvents={{escape: true, clickOutsideAnchor: true }}
            afterHide={() => setWatched(history.some(item => item.tmdb_id.toString() === media.id.toString()))}
        >
            {content()}
        </Tooltip>
    )
}

export default WatchPopup;